import React from 'react';
import { Box } from '@mui/material';

interface RouteDiagramProps {
  /**
   * Whether this render arrived VIA THE TAG — a genuine tag tap, of either family.
   *
   * Named for the route and not the verdict on purpose: a Connect tap took the tag
   * route without earning "verified", and this diagram draws routes (see below).
   */
  viaTag: boolean;
}

/**
 * How you got to this certificate, drawn: phone -> tag -> certificate when a tag was
 * tapped, phone -> certificate straight past the tag when it wasn't.
 *
 * BOTH ROUTES ARE ALWAYS DRAWN; EXACTLY ONE IS ACTIVE. That is the whole redesign
 * (2026-08-31). The previous version drew only the route taken and lifted the tag off
 * to one side when it wasn't used, which answered "what happened" but not "compared to
 * what" — and the unverified state is precisely the one where the reader needs the
 * comparison, because what they want to know is what they MISSED. Two routes between
 * the same two endpoints, one lit and one faded, says "there is a better way in and
 * this request did not take it" in a single glance.
 *
 * The tag sits ON the axis, and the direct route arcs AROUND it. This is load-bearing
 * rather than decorative: it makes "bypassed the tag" a literal fact about the picture,
 * and it keeps the tag at the centre of the diagram. Pushed off-axis, the tag reads as
 * an aside again, which is the thing this redesign exists to stop.
 *
 * IT DRAWS THE ROUTE, NOT THE VERDICT — and that constraint still governs everything
 * here. The honest bound on our claim is narrow: a valid token proves this RENDER
 * followed a genuine tag challenge, not that the object in someone's hand is the tagged
 * one. Prose can hedge that; a picture cannot. Draw phone -> tag -> ✓ and you have
 * asserted "this object is genuine", which is more than we can prove, and nobody argues
 * with a diagram the way they argue with a sentence. So there is no tick, no shield, no
 * "authentic" stamp anywhere in here. Both states are literally true statements about
 * the path taken, which is the thing we actually know.
 *
 * It also deliberately draws the INTERACTION, not the cryptography. No keys, no
 * nonces, no arrows labelled challenge/response. That would be the ER-diagram failure
 * mode — technical furniture for a consumer on a phone who wants reassurance, not a
 * protocol — and it would invite scrutiny of exactly the claim we just bounded.
 *
 * DIM IS OPACITY, NOT A HUE. The inactive route drops to 30% of the same mono line
 * colour. Nothing in this diagram is ever red, amber or brand: colour would be the
 * diagram passing a verdict, which is the one thing the paragraph above forbids it from
 * doing, and "unverified" is an ordinary outcome of a shared link rather than a fault
 * to flag. Mono-line in the inherited text colour also keeps it sitting with the copy
 * rather than reading as an illustration pasted in from another system.
 *
 * ⚠️ THE MOTION IS A DELIBERATE REVERSAL of this file's former "not animated, on
 * purpose" note, and only on the unverified branch. The old reasoning — that a drawer
 * which opens and then performs reads as fidgety — still holds for the tapped states,
 * which is why they stay still: their job is reassurance, and reassurance does not
 * fidget. The unverified branch is doing different work. Its whole message is that the
 * request travelled STRAIGHT FROM THE PHONE TO THE CERTIFICATE and never touched the
 * tag, and a still dashed line states an association where a moving one states a
 * direction. So the active direct route marches, and the arrowhead names which way. The
 * inactive route never moves in either state: motion on a faded path would pull the eye
 * to the route that did not happen.
 *
 * Animation mechanics, and the trap: this is a pure CSS `@keyframes` marching-dash on
 * `stroke-dashoffset`, NOT a WAAPI or rAF-driven effect. In the dev preview pane
 * `requestAnimationFrame` and `ResizeObserver` never fire, so anything driven by either
 * silently renders one static frame while looking correct in a screenshot. CSS
 * keyframes do run there. It is also `infinite` with NO fill mode, so in any
 * environment where animations do not tick at all the line simply renders at offset 0
 * — an ordinary dashed line, which is the old behaviour and always correct. Never give
 * it `backwards`/`both` fill (see the note in CertificatesGrid for what that costs).
 */

/** One dash + one gap of the `3 6` pattern. Marching a whole cycle is seamless. */
const DASH_CYCLE = 9;

/**
 * Negative offset marches the pattern ALONG the path (phone -> certificate); a positive
 * one would run it backwards, which would say the opposite of the sentence beside it.
 */
/**
 * One `DASH_CYCLE` per this long. 800ms (≈11px/s) read as hurried — a line that
 * busy competes with the copy beside it for attention, and the copy is the thing
 * carrying the meaning. At 1800ms (≈5px/s) the direction is still unmistakable
 * while the diagram sits quietly under the sentence it illustrates.
 */
const FLOW_CYCLE_MS = 1800;

const FLOW_SX = {
  '@keyframes veraRouteFlow': {
    from: { strokeDashoffset: 0 },
    to: { strokeDashoffset: -DASH_CYCLE },
  },
  animation: `veraRouteFlow ${FLOW_CYCLE_MS}ms linear infinite`,
  // The motion reinforces the copy, it does not carry meaning the copy lacks: the
  // arrowhead and the dimming both survive, so `none` loses nothing.
  '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
} as const;

/**
 * ── THE VIA-TAG ROUTE LOOPS TOO ──────────────────────────────────────────────────
 *
 * This file used to say the tapped states stay still because "their job is
 * reassurance, and reassurance does not fidget", and a first pass at motion here
 * kept half of that by running ONE traverse and stopping — a completed journey
 * told once.
 *
 * Looking at it settled it the other way, and the earlier argument was wrong about
 * what the picture is for. A single traverse is only seen by someone already
 * watching when the drawer opens; anyone who reads the heading first, or opens the
 * drawer and then looks down, finds a still diagram and never learns that the line
 * had a direction at all. The motion is not a flourish on top of the explanation,
 * it IS the explanation — "the request went through the tag" is the whole point of
 * the via-tag drawing — so it has to be there whenever someone looks.
 *
 * What the old reasoning got right is that it must not be BUSY. So the loop is
 * mostly rest: the pulse travels for `PULSE_TRAVEL_MS` and the cycle then waits out
 * the remainder before going again, which reads as a steady heartbeat rather than a
 * conveyor belt. Both branches now loop, and the difference between them is the
 * tempo, not whether anything moves.
 *
 * It still draws the ROUTE and not the VERDICT, which is the constraint the docblock
 * above puts beyond negotiation. The ring is a tag ANSWERING, not a tick: no check
 * mark, no shield, no colour. "The pulse passed through the tag" is a true statement
 * about the path; "the object is genuine" is not ours to draw.
 */

/** Travel time, phone to certificate. Was 1500 — slower reads as deliberate. */
const PULSE_TRAVEL_MS = 2400;
/** The rest between passes. Longer than the travel, so the diagram is mostly still. */
const PULSE_REST_MS = 2600;
const PULSE_CYCLE_MS = PULSE_TRAVEL_MS + PULSE_REST_MS;
/** The axis the pulse rides: phone edge (50) to certificate edge (248). */
const PULSE_PATH_LENGTH = 198;
/** The bright segment's length. Short enough to read as a travelling pulse. */
const PULSE_LENGTH = 22;
/** Lets the drawer settle before anything moves. */
const PULSE_DELAY_MS = 320;

/** What fraction of the cycle is spent travelling; the rest is the pause. */
const TRAVEL_PCT = (PULSE_TRAVEL_MS / PULSE_CYCLE_MS) * 100;

/**
 * Where in the cycle the pulse's centre is over the mark at x=150 — `(150 - 50) / 198`
 * of the way along, minus half the pulse so the ring fires as it ARRIVES rather than
 * after it has gone.
 *
 * This is EXACT rather than approximate only because the travel is now `linear`. The
 * one-shot version eased it (`cubic-bezier(0.4, 0, 0.3, 1)`), which put the pulse
 * somewhere else at this instant than the arithmetic says and left the ring visibly
 * early. Easing also costs more on a loop than it buys: a repeating ease decelerates
 * into every pass and so reads as a hesitation rather than as travel.
 */
const RING_AT_PCT =
  TRAVEL_PCT * ((150 - 50 - PULSE_LENGTH / 2) / PULSE_PATH_LENGTH);
const RING_MS = 620;
/** Where the ring stops being invisible — one frame's worth before it fires. */
const RING_HOLD_PCT = RING_AT_PCT - (60 / PULSE_CYCLE_MS) * 100;
const RING_END_PCT = RING_AT_PCT + (RING_MS / PULSE_CYCLE_MS) * 100;

/*
 * `backwards` fill, not `both` and not bare `infinite`. The difference matters in
 * exactly one place — an environment where animations never tick at all:
 *
 * - bare `infinite` renders the UNANIMATED value, a 22px stub parked at x=50..72,
 *   which is a stray mark nobody can interpret.
 * - `backwards` holds the FIRST keyframe through the delay and through any such
 *   environment. At 0% the dash is offset clear of the start, so it is invisible and
 *   the diagram is exactly what it was before this existed.
 * - `both` would add a forwards fill, which an infinite animation never reaches.
 *
 * (The marching dash above is deliberately the opposite — no fill — because ITS
 * unanimated value is an ordinary dashed line, which is meaningful on its own.)
 */
/*
 * ⚠️ THE PULSE NEEDS CONTRAST, NOT JUST WIDTH. The first build drew it at 2.5 against
 * the route's 1.5 in the SAME `currentColor`, which is about one device pixel of
 * difference at the size this renders — it animated correctly, registered in
 * `getAnimations()`, and was invisible to look at. (The chip's sheen failed in exactly
 * the same way and for the same reason: a mechanism that provably runs is not yet a
 * thing anyone can see, and only looking at it tells them apart.)
 *
 * So the pulse is both WIDER and DARKER: the diagram as a whole is drawn in
 * `text.secondary`, and the pulse overrides to `text.primary`. That stays inside the
 * mono-line constraint the docblock sets — it is the same ink at a different weight,
 * not a hue passing a verdict — while giving the travelling segment somewhere to
 * stand out from.
 */
const PULSE_SX = {
  color: 'text.primary',
  /*
   * ⚠️ `!important`, deliberately, and it is the ONLY way to set a stroke width in
   * this app. `createMuiTheme`'s `MuiCssBaseline` carries
   * `'svg, svg *': { strokeWidth: '1.25 !important' }`, so a `strokeWidth={4}` prop
   * is inert and so is a plain `strokeWidth: 4` in `sx` — both were tried here and
   * both measured `1.25px`, which is why the pulse first shipped indistinguishable
   * from the line it rides on. `IconBadge` documents the same override from the
   * other side and concludes its own `strokeWidth` knob is a no-op.
   *
   * The exception is narrow and this is the case for it: that override exists to
   * normalise ICON weight across the app, and everything it was written for is an
   * icon. This is a diagram, and its pulse is the one element here whose weight is
   * carrying meaning rather than style — it has to read as a heavier mark moving
   * along a lighter line. Colour alone (`text.primary` against the route's
   * `text.secondary`) does most of the work; the width is what stops a 1.25px dark
   * segment reading as a smudge on a 1.25px grey one.
   *
   * Do NOT take this as licence to override the weight of an icon. If that global
   * rule is ever lifted, this line keeps its intent and `IconBadge`'s knob comes
   * back to life — neither needs revisiting.
   */
  strokeWidth: '4px !important',
  '@keyframes veraRoutePulse': {
    '0%': { strokeDashoffset: PULSE_LENGTH },
    [`${TRAVEL_PCT}%`]: { strokeDashoffset: -PULSE_PATH_LENGTH },
    '100%': { strokeDashoffset: -PULSE_PATH_LENGTH },
  },
  animation: `veraRoutePulse ${PULSE_CYCLE_MS}ms linear ${PULSE_DELAY_MS}ms infinite backwards`,
  '@media (prefers-reduced-motion: reduce)': { animation: 'none', opacity: 0 },
} as const;

/*
 * Driven off the SAME cycle length as the pulse rather than its own short animation
 * with a delay. Two `infinite` animations of different durations drift out of phase
 * within a few passes — the ring would fire before the pulse arrives, then after it,
 * then at random — so the ring's whole timing lives inside the pulse's cycle as
 * percentages and the two can never separate.
 */
const RING_SX = {
  '@keyframes veraRouteRing': {
    /*
     * The HOLD at 0 up to the moment before arrival is load-bearing. Without it the
     * two stops either side interpolate, so the ring sits faintly visible over the
     * mark for the whole approach (measured: opacity 0.15 a tenth of the way in) —
     * which reads as the tag already answering before anything has reached it.
     */
    '0%': { transform: 'scale(1)', opacity: 0 },
    [`${RING_HOLD_PCT}%`]: { transform: 'scale(1)', opacity: 0 },
    [`${RING_AT_PCT}%`]: { transform: 'scale(1)', opacity: 0.55 },
    [`${RING_END_PCT}%`]: { transform: 'scale(1.5)', opacity: 0 },
    '100%': { transform: 'scale(1.5)', opacity: 0 },
  },
  /* `fill-box` + a centre origin, or the scale is taken about the SVG viewBox's
     origin and the ring flies off to the top-left instead of expanding in place. */
  transformBox: 'fill-box',
  transformOrigin: 'center',
  animation: `veraRouteRing ${PULSE_CYCLE_MS}ms linear ${PULSE_DELAY_MS}ms infinite backwards`,
  '@media (prefers-reduced-motion: reduce)': { animation: 'none', opacity: 0 },
} as const;

/** Faded, not recoloured — see the docblock. */
const INACTIVE_OPACITY = 0.3;

/** The Vera mark, scaled from the same path the loader uses. */
const VERA_MARK =
  'M84.0345 79.33L39.6849 1.77528C39.3364 1.17763 38.6829 0.802368 37.9568 0.802368H2.39299C0.897245 0.802368 -0.0466709 2.34512 0.679419 3.59601L84.0345 147.031C84.7896 148.323 86.721 148.323 87.4616 147.031L170.817 3.59601C171.543 2.34512 170.599 0.802368 169.103 0.802368H133.539C132.828 0.802368 132.16 1.17763 131.811 1.77528L87.4616 79.33C86.721 80.6365 84.7606 80.6365 84.02 79.33H84.0345Z';

export const RouteDiagram: React.FC<RouteDiagramProps> = ({ viaTag }) => (
  <Box
    aria-hidden
    sx={{
      color: 'text.secondary',
      display: 'flex',
      justifyContent: 'center',
      py: 1,
    }}
  >
    <Box
      component="svg"
      viewBox="0 0 300 88"
      sx={{ width: '100%', maxWidth: 300, height: 'auto', display: 'block' }}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {/* `orient="auto"` rotates the head to whichever route it terminates, so the
          straight run and the arc get the same glyph without hand-computed trig.
          `userSpaceOnUse` stops it scaling with stroke-width. */}
      <defs>
        <marker
          id="veraRouteArrowHead"
          viewBox="0 0 10 10"
          refX={8.4}
          refY={5}
          markerWidth={10}
          markerHeight={10}
          markerUnits="userSpaceOnUse"
          orient="auto"
        >
          <path
            d="M3.2 1.6 L8 5 L3.2 8.4"
            fill="none"
            stroke="currentColor"
            strokeWidth={1.5}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </marker>
      </defs>

      {/* phone */}
      <rect x="12" y="12" width="38" height="60" rx="7" />
      <line x1="25" y1="20" x2="37" y2="20" />

      {/* certificate */}
      <rect x="250" y="12" width="42" height="60" rx="5" />
      <line x1="259" y1="28" x2="283" y2="28" />
      <line x1="259" y1="38" x2="283" y2="38" />
      <line x1="259" y1="48" x2="274" y2="48" />

      {/* ── the VIA-TAG route: straight through the tag, on the axis ───────────── */}
      <g opacity={viaTag ? 1 : INACTIVE_OPACITY}>
        <line x1="50" y1="42" x2="126" y2="42" />
        <line
          x1="174"
          y1="42"
          x2="248"
          y2="42"
          markerEnd={viaTag ? 'url(#veraRouteArrowHead)' : undefined}
        />
        <circle cx="150" cy="42" r="22" />
        <path
          d={VERA_MARK}
          transform="translate(139.5 35) scale(0.128)"
          fill="currentColor"
          stroke="none"
        />
        {/* contact ticks — a tap, not a signal broadcast */}
        <path d="M92 30 L98 24" opacity="0.5" />
        <path d="M92 54 L98 60" opacity="0.5" />

        {viaTag && (
          <>
            {/* The tag answering as the pulse arrives. Drawn BEFORE the pulse so the
                pulse rides over it, and inside the via-tag group so it inherits the
                same opacity treatment if that ever changes. */}
            <Box
              component="circle"
              cx="150"
              cy="42"
              r="22"
              fill="none"
              stroke="currentColor"
              sx={RING_SX}
            />
            {/* The request itself, travelling the axis. A dash on a copy of the
                route rather than a moving <circle>: `offset-path` is the obvious tool
                and is the one with the patchy support, while an animated
                `stroke-dashoffset` works everywhere this app runs. */}
            <Box
              component="line"
              x1="50"
              y1="42"
              x2="248"
              y2="42"
              strokeDasharray={`${PULSE_LENGTH} ${PULSE_PATH_LENGTH}`}
              sx={PULSE_SX}
            />
          </>
        )}
      </g>

      {/* ── the DIRECT route: around the tag, never touching it ────────────────── */}
      <Box
        component="path"
        d="M50 52 Q150 96 250 52"
        strokeDasharray={`3 ${DASH_CYCLE - 3}`}
        markerEnd={viaTag ? undefined : 'url(#veraRouteArrowHead)'}
        opacity={viaTag ? INACTIVE_OPACITY : 1}
        sx={viaTag ? undefined : FLOW_SX}
      />
    </Box>
  </Box>
);

export default RouteDiagram;
